import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Tag } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TRADE } from "@/constants/testIds";

export default function PlaceOrder() {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState("tank");
  const [qty, setQty] = useState(5);
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/trade/catalog").then(({ data }) => setCatalog(data));
  }, []);

  if (!catalog) {
    return (
      <PortalShell title="Place wholesale order" subtitle="Trade ordering">
        <div className="text-slate-500">Loading catalog...</div>
      </PortalShell>
    );
  }

  const item = catalog[selected];
  const total = (item.wholesale * qty).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/trade/orders", {
        test_id: selected, test_name: item.name, quantity: Number(qty),
        base_price: item.wholesale, total: Number(total),
        client_name: clientName, client_address: clientAddress,
        white_label: whiteLabel, notes: notes || null,
      });
      toast.success(`Order placed: ${data.reference}`);
      navigate("/trade/orders");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalShell title="Place wholesale order" subtitle="Trade ordering">
      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-600 mb-3">Step 1 · Choose test</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {Object.entries(catalog).map(([id, c]) => (
                <button
                  type="button"
                  key={id}
                  data-testid={TRADE.placeOrderTestId(id)}
                  onClick={() => setSelected(id)}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${
                    selected === id ? "border-cyan-500 bg-cyan-50/40 shadow-md" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-600">{id}</div>
                  <div className="font-display font-semibold text-slate-900 mt-1 text-sm leading-tight">{c.name}</div>
                  <div className="mt-2 flex items-baseline gap-1.5">
                    <span className="text-xl font-display font-bold text-slate-900">${c.wholesale.toFixed(2)}</span>
                    <span className="text-xs text-slate-500">/ sample</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-600 mb-3">Step 2 · Order details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Quantity</Label>
                <Input data-testid={TRADE.placeOrderQuantity} type="number" min={1} max={200} required value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5 h-12 rounded-xl" />
              </div>
              <div className="flex items-center">
                <label className="flex items-start gap-2.5 cursor-pointer mt-6">
                  <Checkbox data-testid={TRADE.placeOrderWhiteLabel} checked={whiteLabel} onCheckedChange={(v) => setWhiteLabel(!!v)} />
                  <span>
                    <span className="font-semibold text-slate-900 text-sm">White-label report</span>
                    <span className="block text-xs text-slate-500">Your branding only — no Aware logos</span>
                  </span>
                </label>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Client name</Label>
                <Input data-testid={TRADE.placeOrderClientName} required value={clientName} onChange={(e) => setClientName(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="End-customer name" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Client address</Label>
                <AddressAutocomplete
                  value={clientAddress}
                  onChange={setClientAddress}
                  placeholder="Start typing the client's address…"
                  className="mt-1.5 h-12 rounded-xl"
                  testId={TRADE.placeOrderClientAddress}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Notes <span className="normal-case text-slate-400">(optional)</span></Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5 rounded-xl" placeholder="Specific concerns, additional parameters, delivery preferences..." />
              </div>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600 mb-4">Order summary</div>
            <div className="space-y-3 pb-4 border-b border-slate-100">
              <div className="flex justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{item.name}</div>
                  <div className="text-xs text-slate-500">${item.wholesale.toFixed(2)} × {qty}</div>
                </div>
                <div className="text-sm font-semibold text-slate-900">${total}</div>
              </div>
              {whiteLabel && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 text-cyan-300 text-[10px] font-bold uppercase tracking-wider">
                  <Tag className="w-3 h-3" /> White label
                </div>
              )}
            </div>
            <div className="mt-4 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-slate-700">Total</span>
              <span className="font-display text-3xl font-bold text-slate-900">${total}</span>
            </div>
            <div className="mt-4 p-3 rounded-xl bg-cyan-50 border border-cyan-100">
              <p className="text-xs text-cyan-800">Invoiced on net-7 terms. No upfront card charge.</p>
            </div>
            <Button data-testid={TRADE.placeOrderSubmit} disabled={submitting} type="submit" size="lg" className="w-full mt-5 btn-aqua rounded-full font-semibold">
              {submitting ? "Placing..." : <>Place order <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </div>
        </aside>
      </form>
    </PortalShell>
  );
}
